import { useState, useMemo } from "react";
import { useClients } from "@/hooks/useClients";
import { useCreateClient } from "@/hooks/useCreateClient";
import { useUpdateClient } from "@/hooks/useUpdateClient";
import { useDeleteClient } from "@/hooks/useDeleteClient";
import { Client } from "@/types/client";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Users, Building, Phone, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { LoadingSpinnerWithText } from "@/components/ui/loading-spinner";
import { useNavigate } from "react-router-dom";
import { ClientInlineFilter } from "@/components/clients/ClientInlineFilter";
import { ClientStatusStats } from "@/components/clients/ClientStatusStats";
import { ViewToggle } from "@/components/shared/ViewToggle";
import { ClientsTable } from "@/components/clients/ClientsTable";
import { ClientsCardsView } from "@/components/clients/ClientsCardsView";
import { ClientEditDialog } from "@/components/clients/ClientEditDialog";
import { ClientFilesDialog } from "@/components/clients/ClientFilesDialog";
import { PageWrapper } from "@/components/layout/PageWrapper";

const ClientsPage = () => {
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState<Client | undefined>();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isFilesDialogOpen, setIsFilesDialogOpen] = useState(false);
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [filters, setFilters] = useState({
    name_ar: "",
    name_en: "",
    mobile: "",
    company: "",
    status: "",
    generalSearch: "",
    created_at_from: "",
    created_at_to: ""
  });
  const {
    data: clients = [],
    isLoading,
    error,
    refetch
  } = useClients();

  // Filter clients based on the filter criteria
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      const matchesGeneralSearch = filters.generalSearch === "" || client.name_ar?.toLowerCase().includes(filters.generalSearch.toLowerCase()) || client.name_en?.toLowerCase().includes(filters.generalSearch.toLowerCase()) || client.mobile?.includes(filters.generalSearch) || client.company?.toLowerCase().includes(filters.generalSearch.toLowerCase());
      const matchesNameAr = filters.name_ar === "" || client.name_ar?.toLowerCase().includes(filters.name_ar.toLowerCase());
      const matchesNameEn = filters.name_en === "" || client.name_en?.toLowerCase().includes(filters.name_en.toLowerCase());
      const matchesMobile = filters.mobile === "" || client.mobile?.includes(filters.mobile);
      const matchesCompany = filters.company === "" || client.company?.toLowerCase().includes(filters.company.toLowerCase());
      const matchesStatus = filters.status === "" || client.status === filters.status;
      
      // Date filters
      const clientDate = new Date(client.created_at).toISOString().split('T')[0];
      const matchesDateFrom = filters.created_at_from === "" || clientDate >= filters.created_at_from;
      const matchesDateTo = filters.created_at_to === "" || clientDate <= filters.created_at_to;
      
      return matchesGeneralSearch && matchesNameAr && matchesNameEn && matchesMobile && matchesCompany && matchesStatus && matchesDateFrom && matchesDateTo;
    });
  }, [clients, filters]);

  // Calculate status statistics
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    filteredClients.forEach(client => {
      stats[client.status] = (stats[client.status] || 0) + 1;
    });
    return stats;
  }, [filteredClients]);

  // Status options for the filter
  const statusOptions = [{
    value: 'confirmed',
    label: 'مؤكد',
    color: 'bg-green-100 text-green-800'
  }, {
    value: 'waiting',
    label: 'في الانتظار',
    color: 'bg-yellow-100 text-yellow-800'
  }, {
    value: 'in_progress',
    label: 'قيد التنفيذ',
    color: 'bg-blue-100 text-blue-800'
  }, {
    value: 'waiting_supplier',
    label: 'انتظار المورد',
    color: 'bg-orange-100 text-orange-800'
  }, {
    value: 'cancelled',
    label: 'ملغي',
    color: 'bg-red-100 text-red-800'
  }, {
    value: 'pricing_requested',
    label: 'طلب تسعير',
    color: 'bg-indigo-100 text-indigo-800'
  }, {
    value: 'pricing_sent',
    label: 'تم إرسال التسعير',
    color: 'bg-cyan-100 text-cyan-800'
  }, {
    value: 'booking_confirmed',
    label: 'تأكيد الحجز',
    color: 'bg-emerald-100 text-emerald-800'
  }, {
    value: 'traveling',
    label: 'يسافر',
    color: 'bg-purple-100 text-purple-800'
  }, {
    value: 'customer_service',
    label: 'خدمة العملاء',
    color: 'bg-pink-100 text-pink-800'
  }];
  const createClientMutation = useCreateClient();
  const updateClientMutation = useUpdateClient();
  const deleteClientMutation = useDeleteClient();
  const handleAddClient = () => {
    navigate('/clients/add');
  };
  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    setIsEditDialogOpen(true);
  };

  const handleShowFiles = (client: Client) => {
    setSelectedClient(client);
    setIsFilesDialogOpen(true);
  };
  const handleDeleteClient = async (clientId: string) => {
    if (confirm("هل أنت متأكد من حذف هذا العميل؟")) {
      try {
        await deleteClientMutation.mutateAsync(clientId);
        toast.success("تم حذف العميل بنجاح");
      } catch (error) {
        toast.error("حدث خطأ أثناء حذف العميل");
      }
    }
  };
  const getStatusColor = (status: string) => {
    const colors = {
      'confirmed': 'bg-green-100 text-green-800',
      'waiting': 'bg-yellow-100 text-yellow-800',
      'in_progress': 'bg-blue-100 text-blue-800',
      'waiting_supplier': 'bg-orange-100 text-orange-800',
      'cancelled': 'bg-red-100 text-red-800',
      'pricing_requested': 'bg-indigo-100 text-indigo-800',
      'pricing_sent': 'bg-cyan-100 text-cyan-800',
      'booking_confirmed': 'bg-emerald-100 text-emerald-800',
      'traveling': 'bg-purple-100 text-purple-800',
      'customer_service': 'bg-pink-100 text-pink-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };
  const getStatusLabel = (status: string) => {
    const statusOption = statusOptions.find(option => option.value === status);
    return statusOption ? statusOption.label : status;
  };
  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };
  if (isLoading) {
    return (
      <PageWrapper maxWidth="2xl">
        <LoadingSpinnerWithText text="جاري تحميل العملاء..." />
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper maxWidth="2xl">
        <div className="text-center text-destructive">
          <p>حدث خطأ في تحميل البيانات</p>
          <Button onClick={() => refetch()} variant="outline" className="mt-4">
            إعادة المحاولة
          </Button>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      title="إدارة العملاء"
      subtitle="إدارة وعرض جميع بيانات العملاء"
      maxWidth="2xl"
      actions={
        <>
          <Button 
            onClick={handleAddClient} 
            size="sm" 
            className="gap-2 w-full sm:w-auto"
          >
            <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">إضافة عميل جديد</span>
            <span className="sm:hidden">إضافة</span>
          </Button>
          
          {/* الفلتر المتقدم - مخفي على الموبايل الصغير جداً */}
          <div className="hidden md:block">
            <ClientInlineFilter 
              onFilterChange={handleFilterChange} 
              statusOptions={statusOptions}
              currentFilters={filters}
            />
          </div>
          
          {/* أزرار العرض */}
          <ViewToggle view={view} onViewChange={setView} />
        </>
      }
    >
      {/* البحث السريع */}
      <div className="relative w-full sm:max-w-md">
        <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-3 w-3 sm:h-4 sm:w-4" />
        <Input
          placeholder="البحث السريع..."
          value={filters.generalSearch}
          onChange={(e) => handleFilterChange({ ...filters, generalSearch: e.target.value })}
          className="pr-9 sm:pr-10 text-sm"
        />
      </div>

      {/* Status Statistics */}
      <ClientStatusStats 
        statusStats={statusStats} 
        getStatusLabel={getStatusLabel} 
        getStatusColor={getStatusColor} 
        className="animate-fade-in-content"
      />

      {/* View Content */}
      {view === 'table' ? <ClientsTable clients={filteredClients} onEditClient={handleEditClient} onDeleteClient={handleDeleteClient} onAddClient={handleAddClient} onShowFiles={handleShowFiles} /> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ClientsCardsView clients={filteredClients} onEditClient={handleEditClient} onDeleteClient={handleDeleteClient} onShowFiles={handleShowFiles} />
        </div>}

      {/* Empty State */}
      {filteredClients.length === 0 && clients.length > 0 && <Card className="text-center py-12">
          <CardContent>
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">لا توجد نتائج</h3>
            <p className="text-muted-foreground mb-4">لا توجد عملاء تطابق معايير البحث</p>
            <Button variant="outline" onClick={() => setFilters({
          name_ar: "",
          name_en: "",
          mobile: "",
          company: "",
          status: "",
          generalSearch: "",
          created_at_from: "",
          created_at_to: ""
        })}>
              مسح جميع الفلاتر
            </Button>
          </CardContent>
        </Card>}
      
      {clients.length === 0 && <Card className="text-center py-12">
          <CardContent>
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">لا يوجد عملاء</h3>
            <p className="text-muted-foreground mb-4">ابدأ بإضافة عميل جديد</p>
            <Button onClick={handleAddClient}>
              <Plus className="w-4 h-4 ml-2" />
              إضافة عميل جديد
            </Button>
          </CardContent>
        </Card>}

      {/* Edit Client Dialog */}
      <ClientEditDialog
        client={selectedClient || null}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={() => {
          refetch();
          setSelectedClient(undefined);
        }}
      />

      {/* Files Dialog */}
      <ClientFilesDialog
        client={selectedClient || null}
        open={isFilesDialogOpen}
        onOpenChange={setIsFilesDialogOpen}
      />
    </PageWrapper>
  );
};
export default ClientsPage;